"use client";

import { useTranslations } from "use-intl";

import { cn } from "@/lib/utils";

const GRADE_COLORS: Record<string, string> = {
  Mint: "bg-emerald-500/15 text-emerald-400 border-emerald-500/20",
  NM: "bg-green-500/15 text-green-400 border-green-500/20",
  "VG+": "bg-lime-500/15 text-lime-400 border-lime-500/20",
  VG: "bg-yellow-500/15 text-yellow-400 border-yellow-500/20",
  "G+": "bg-orange-500/15 text-orange-400 border-orange-500/20",
  G: "bg-orange-600/15 text-orange-500 border-orange-600/20",
  Fair: "bg-red-500/15 text-red-400 border-red-500/20",
  Poor: "bg-red-700/15 text-red-500 border-red-700/20",
};

const DEFAULT_COLOR = "bg-muted text-muted-foreground border-border";

interface GradeBadgeProps {
  recordGrade?: string | null;
  sleeveGrade?: string | null;
  className?: string;
}

export function GradeBadge({ recordGrade, sleeveGrade, className }: GradeBadgeProps) {
  const t = useTranslations("shared.collection");
  const tg = useTranslations("shared.grades");

  if (!recordGrade && !sleeveGrade) {
    return (
      <span className={cn("text-[11px] text-muted-foreground/60", className)}>{t("noGrade")}</span>
    );
  }

  const formatGrade = (grade: string) => {
    const shortKey = `${grade}Short` as Parameters<typeof tg>[0];
    return tg.has(shortKey) ? tg(shortKey) : grade;
  };

  // Use the worst grade for the combined color
  const grades = ["Mint", "NM", "VG+", "VG", "G+", "G", "Fair", "Poor"];
  const worstGrade = [recordGrade, sleeveGrade]
    .filter(Boolean)
    .sort((a, b) => grades.indexOf(b!) - grades.indexOf(a!))[0];

  const colorClass = (worstGrade && GRADE_COLORS[worstGrade]) || DEFAULT_COLOR;

  const recordLabel = recordGrade ? formatGrade(recordGrade) : null;
  const sleeveLabel = sleeveGrade ? formatGrade(sleeveGrade) : null;

  const display =
    recordLabel && sleeveLabel ? `${recordLabel}/${sleeveLabel}` : (recordLabel ?? sleeveLabel);

  return (
    <span
      className={cn(
        "inline-flex items-center px-1.5 py-0.5 rounded text-[11px] font-semibold border leading-none",
        colorClass,
        className,
      )}
    >
      {display}
    </span>
  );
}
